import dayjs from 'dayjs';

import { AppointmentEvent, TravelEvent, UnallocatedAppointment, Worker } from '~weekly-planner/lib/common';

// Compute current-week dates so spoofed data always falls in the visible week
const today = dayjs();
const daysFromMonday = today.day() === 0 ? 6 : today.day() - 1;
const thisMonday = today.subtract(daysFromMonday, 'day');

const MON = thisMonday.format('YYYY-MM-DD');
const TUE = thisMonday.add(1, 'day').format('YYYY-MM-DD');
const WED = thisMonday.add(2, 'day').format('YYYY-MM-DD');
const THU = thisMonday.add(3, 'day').format('YYYY-MM-DD');
const FRI = thisMonday.add(4, 'day').format('YYYY-MM-DD');

const SOCIAL_SUPPORT = { id: 1, name: 'Social Support', colour: '#3498db' };
const PERSONAL_CARE = { id: 2, name: 'Personal Care', colour: '#e67e22' };
const COMMUNITY_ACCESS = { id: 3, name: 'Community Access', colour: '#2ecc71' };
const DOMESTIC_ASSISTANCE = { id: 4, name: 'Domestic Assistance', colour: '#9b59b6' };

const APPROVED_STATUS = { id: 1, name: 'Approved', colour: '#27ae60' };

const makeClient = (id: string, full_name: string, suburb: string) => ({
  id,
  full_name,
  address: `${id} Example Street`,
  suburb,
  postcode: '4000',
  state: 'QLD',
  referrer_name: '',
  referrer_number: 0,
  events: {},
});

const makeAppointment = (
  id: number,
  date: string,
  userId: string,
  clientId: string,
  clientName: string,
  clientSuburb: string,
  startTime: string,
  endTime: string,
  durationMins: number,
  serviceType: typeof SOCIAL_SUPPORT,
): AppointmentEvent => ({
  type: 'appointment',
  totals: {
    simple: durationMins,
    appointments: durationMins,
    predicted: durationMins,
  },
  data: {
    key: id,
    id,
    date,
    user_id: parseInt(userId),
    repeat: false,
    parent_repeat_id: null,
    start_time: startTime,
    end_time: endTime,
    duration: durationMins,
    flexibility: false,
    qualification_level: [],
    notes: '',
    status: APPROVED_STATUS,
    client: makeClient(clientId, clientName, clientSuburb),
    service_type: serviceType,
    is_bidding: false,
    bidding_user_ids: [],
    rejected_user_ids: [],
  },
});

const makeTravel = (
  type: 'start' | 'end',
  fromTime: string | null,
  toTime: string | null,
  distanceKm: number,
): TravelEvent => ({
  type: 'travel',
  totals: {},
  data: {
    type,
    from: { time: fromTime, address: '' },
    to: { time: toTime, address: '' },
    distance: distanceKm,
    duration: Math.round(distanceKm * 0.9),
  },
});

// ---------------------------------------------------------------------------
// Spoofed workers
// ---------------------------------------------------------------------------

export const SPOOFED_WORKERS: Worker[] = [
  {
    id: '1',
    full_name: 'Kim Barton',
    gender: { id: 2, name: 'Female' },
    age: 34,
    qualifications: ['cert-3'],
    rostering_notes: '',
    hours_min: 20,
    hours_max: 38,
    availability: {
      Monday: { start: '08:00', end: '20:00' },
      Tuesday: { start: '08:00', end: '20:00' },
      Wednesday: { start: '08:00', end: '20:00' },
      Thursday: { start: '08:00', end: '20:00' },
      Friday: { start: '08:00', end: '20:00' },
    },
    events: {
      [MON]: [
        makeTravel('start', null, '09:00', 15.3),
        makeAppointment(1001, MON, '1', 'c1', 'Sarah Johnson', 'Newstead', '09:00', '11:00', 120, PERSONAL_CARE),
        makeTravel('end', '11:00', null, 15.3),
      ],
      [TUE]: [],
      [WED]: [],
      [THU]: [
        makeTravel('start', null, '15:45', 69.69),
        makeAppointment(1002, THU, '1', 'c2', 'Alonzo Hale', 'Carindale', '15:45', '19:30', 225, SOCIAL_SUPPORT),
        makeTravel('end', '19:30', null, 69.69),
      ],
      [FRI]: [],
    },
  },
  {
    id: '2',
    full_name: 'Steven Parko',
    gender: { id: 1, name: 'Male' },
    age: 27,
    qualifications: ['cert-3', 'cert-4'],
    rostering_notes: '',
    hours_min: 15,
    hours_max: 30,
    availability: {
      Monday: { start: '07:00', end: '18:00' },
      Tuesday: { start: '07:00', end: '18:00' },
      Wednesday: { start: '07:00', end: '18:00' },
      Thursday: { start: '07:00', end: '18:00' },
    },
    events: {
      [MON]: [],
      [TUE]: [
        makeTravel('start', null, '10:00', 12.5),
        makeAppointment(1003, TUE, '2', 'c3', 'Michael Brown', 'Woolloongabba', '10:00', '14:00', 240, COMMUNITY_ACCESS),
        makeTravel('end', '14:00', null, 12.5),
      ],
      [WED]: [
        makeTravel('start', null, '08:30', 8.2),
        makeAppointment(1004, WED, '2', 'c4', 'Helen Torres', 'Kangaroo Point', '08:30', '10:30', 120, DOMESTIC_ASSISTANCE),
        makeTravel('end', '10:30', null, 8.2),
      ],
      [THU]: [],
      [FRI]: [],
    },
  },
  {
    id: '3',
    full_name: 'Lockout Test',
    gender: { id: 1, name: 'Male' },
    age: 45,
    qualifications: [],
    rostering_notes: '',
    hours_min: 0,
    hours_max: 0,
    availability: {},
    events: {
      [MON]: [],
      [TUE]: [],
      [WED]: [],
      [THU]: [],
      [FRI]: [],
    },
  },
  {
    id: '4',
    full_name: 'Group Session User',
    gender: { id: 1, name: 'Male' },
    age: 30,
    qualifications: ['cert-3'],
    rostering_notes: '',
    hours_min: 10,
    hours_max: 25,
    availability: {
      Monday: { start: '09:00', end: '17:00' },
      Wednesday: { start: '09:00', end: '17:00' },
      Friday: { start: '09:00', end: '17:00' },
    },
    events: {
      [MON]: [],
      [TUE]: [],
      [WED]: [],
      [THU]: [],
      [FRI]: [],
    },
  },
];

// ---------------------------------------------------------------------------
// Spoofed unallocated appointments
// ---------------------------------------------------------------------------

const makeUnallocated = (
  id: number,
  date: string,
  clientName: string,
  suburb: string,
  startTime: string,
  endTime: string,
  durationMins: number,
  serviceType: typeof SOCIAL_SUPPORT,
): UnallocatedAppointment => ({
  key: id,
  id,
  date,
  user_id: null,
  repeat: false,
  parent_repeat_id: null,
  start_time: startTime,
  end_time: endTime,
  duration: durationMins,
  flexibility: true,
  qualification_level: [],
  notes: '',
  status: APPROVED_STATUS,
  client: makeClient(`uc${id}`, clientName, suburb),
  service_type: serviceType,
  is_bidding: false,
  is_unallocated: true,
  bidding_user_ids: [],
  rejected_user_ids: [],
  preferred_user: null,
});

export const SPOOFED_UNALLOCATED: UnallocatedAppointment[] = [
  makeUnallocated(2001, TUE, 'Emma Davis', 'Fortitude Valley', '14:00', '17:30', 210, SOCIAL_SUPPORT),
  makeUnallocated(2002, WED, 'Robert Wilson', 'Spring Hill', '08:00', '10:00', 120, PERSONAL_CARE),
  makeUnallocated(2003, FRI, 'Isabella Chen', 'Paddington', '13:00', '16:00', 180, COMMUNITY_ACCESS),
  makeUnallocated(2004, THU, 'Thomas Walker', 'Milton', '09:00', '11:30', 150, DOMESTIC_ASSISTANCE),
];
